import {Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, inject} from '@angular/core';
import {AgGridAngular} from 'ag-grid-angular';
import {
    CellClassParams,
    ColDef,
    ColumnState,
    createGrid,
    ExcelStyle,
    FilterChangedEvent,
    FirstDataRenderedEvent,
    GetRowIdFunc,
    GetRowIdParams,
    GridApi,
    GridState,
    GridOptions,
    GridReadyEvent,
    HeaderValueGetterParams,
    IRowNode,
    IServerSideDatasource,
    IsRowSelectable,
    ModelUpdatedEvent,
    ProcessCellForExportParams,
    RowClickedEvent,
    RowNode,
    RowGroupOpenedEvent,
    RowHeightParams,
    RowNodeTransaction,
    SelectionChangedEvent,
    ServerSideRowSelectionState,
    ServerSideTransactionResult,
    SideBarDef,
    SortChangedEvent,
    StatusPanelDef,
    ToolPanelVisibleChangedEvent
} from 'ag-grid-community';
import {GridClickableButtonComponent} from './grid-clickable-button/grid-clickable-button.component';

import {
    DataExport,
    GridPanel,
    KeysOfUnion,
    PromotionsFe,
    RowModelType,
    TableData,
    TableRow,
    TableRowUpdatable
} from '../../model/model';

import {
    AlertDialogService,
    ConfirmDialogService,
    WaitDialogService,
} from 'src/app/dialogs';

import {first, Observable, tap} from 'rxjs';
import {FormControl} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {GridTraductions} from 'src/app/models/grid-traductions.model';
import {mobileCheck} from 'src/utilities';
import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {Feature, Rest} from 'src/app/models';
import {GridSaveUserPreferenceComponent} from './grid-save-user-preference/grid-save-user-preference.component';
import {
    GridCellFormatterService,
    GridTraductionsService,
    queryFilteredOrdered_FEtoBEConverter
} from './utils';
import {ClickableButton, GridCommon, GridFixedButtons} from './grid.model';
import {autobind} from 'src/app/models/decorators.model';
import {OnDestroyService} from 'src/app/core/services/on-destroy.service';
import {GridService} from './grid.service';
import {ConfigurationManagerService} from 'src/app/core/services/configuration-manager/configuration-manager.service';
import GridUtils, {filterType} from './utils/grid-utils';
import { GridStatusBarComponent } from './grid-status-bar/grid-status-bar.component';
import {SuppliersFields} from "../../../features";
import {CustomersFields} from "../../../features/customers";
import { DevLogger } from '../../dev-logger';
import { TilbyCurrencyPipe } from '@tilby/tilby-ui-lib/pipes/tilby-currency';
import { restManager } from "../../../../../app/ajs-upgraded-providers";
import { LocalizationService } from 'src/app/core/services/locatization.service';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { EntityManagerService, SkeletonGridRowComponent } from 'src/app/core';
import { keyBy } from 'src/app/shared/utils';

@Component({
	standalone: false,
    selector: 'app-grid-component',
    template: '',
    host: {class: 'tw-relative'},
})
export abstract class GridComponent implements OnInit, ClickableButton, GridCommon, OnDestroy {
    protected readonly _snackBar = inject(MatSnackBar);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManager = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    protected readonly gridService = inject(GridService);
    private readonly gridTraductionsService = inject(GridTraductionsService);
    private readonly onDestroyService = inject(OnDestroyService);
    private readonly restMangerService = inject(restManager);
    private readonly translateService = inject(TranslateService);
    private readonly localizationService = inject(LocalizationService);
    private readonly gridCellFormatterService = inject(GridCellFormatterService);
    private readonly itemDataCatalogService = inject(ItemDataCatalogService);
    private readonly waitDialogService = inject(WaitDialogService);
    private readonly entityManagerService = inject(EntityManagerService);
    

    //just have to exist for template (serverside overwrite them)
    protected readonly datasource?: IServerSideDatasource;
    protected readonly cacheBlockSize: any;
    protected readonly loadingCellRenderer: any = SkeletonGridRowComponent;
    public isRowSelectable: IsRowSelectable = (params: IRowNode<TableRow>) => this.isCheckable.call(this, params.data!);
    @Input() feature?: Feature;
    @Input() headerCheckboxSelection: boolean = false;
    @Input() idField?: string;
    @Input() fixedButtons: GridFixedButtons = {
        checkbox: {visible: true, lockPosition: 'left'},
        customButton: {visible: true, cellRenderer: GridClickableButtonComponent, lockPosition: 'right'}
    }
    @Input() exportFormatterOptions? = ({column, value}: ProcessCellForExportParams) => {
        return value
    };
    @Input() selectRowUIEnabled?: boolean = false;
    @Input() rowBuffer: number = 10;
    @Input() maxBlocksInCache: number = 20;
    @Input() masterDetail: boolean = false;
    @Input() detailCellRendererParams: any = {};
    @Input() detailRowAutoHeight: boolean = true;
    @Input() suppressNoRowsOverlay: boolean = false;
    @Input() initialState?: GridState;
    @Input() getRowHeight?: (params: RowHeightParams) => number | undefined | null;
    @Output() areRowsSelected = new EventEmitter<boolean>();
    @Output() rowClicked = new EventEmitter<RowClickedEvent>();
    @Output() rowGroupOpened = new EventEmitter<RowGroupOpenedEvent>();
    @Output() clickableButton = new EventEmitter<Rest>();
    @Output() clickableAction = new EventEmitter<any>();
    @Output() updateChange = new EventEmitter<TableRowUpdatable[]>();
    @Output() deleteChange = new EventEmitter<number[]>();
    @Output() gridReadyEmitter = new EventEmitter<void>();
    @Output() firstDataRenderedEmitter = new EventEmitter<void>();
    @Output() sortChangedEmitter = new EventEmitter<SortChangedEvent>();
    @Output() filterChangedEmitter = new EventEmitter<FilterChangedEvent>();

    /**
     Give access to Ag-grid functions
     */
    @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
    @ViewChild(AgGridAngular, { read: ElementRef }) agGridElement!: ElementRef;
    gridApi?: GridApi;
    headerAction = 'Actions';
    idDefaultLanguage = "en";
    localText?: GridTraductions;
    isMobile = mobileCheck();
    context = {componentParent: this, isMobile: this.isMobile, extras: {}};
    isToastShow = false;
    toastSaveUserPreference?: MatSnackBarRef<any>;

    private pendingCells = new Set<string>();
    private saveQueue: Promise<any> = Promise.resolve();
    private isErrorDialogOpen = false;

    // Con il menù legacy, il pulsante del menù a colonne rimane nascosto finché non si passa sopra con il mouse
    columnMenu: "legacy" | "new" = "legacy";
    public abstract rowModelType: RowModelType; 

    // For accessing the Grid's API
    columnsControl = new FormControl()
    columnDefsInitial: ColDef[] = [];
    // Data that gets displayed in the grid
    _tableData!: TableData;
    get tableData() {
        return this._tableData;
    }

    gridUtils = new GridUtils();

    @Input() set tableData(value: TableData) {
        this._tableData = value;
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.handleFlexVsScroll();
    }

    protected log(...args: any[]){
        DevLogger.log('GRID_SERVER_SIDE_COMPONENT',...args);
    }
    protected error(...args: any[]){
        DevLogger.error('GRID_SERVER_SIDE_COMPONENT',...args);
    }

    initTable() {
        const isClientSide = this.rowModelType === 'clientSide';
        const { checkbox, customButton } = this.fixedButtons;
        const { enableBooleanCheckboxes = false, 
            booleanCheckboxesReadOnly = false,
            forceBooleanColumns = [],
            disabledInternalFilters = [],
        } = this._tableData;

        const dataObj = this._tableData.dataType as any;
        const keys = this._tableData.columnsOrder || Object.keys(dataObj);
        
        this.columnDefs = keys.map((k: string) => {
            const v = this.getDeepValue(dataObj, k);

            const isInternalFilterDisabled = disabledInternalFilters.includes(k);
            const hasCustomRenderer = this._tableData.cellComponentRenderer?.has(k) || false;
            const isForcedBoolean = forceBooleanColumns.includes(k);
            const isBoolean = typeof v === 'boolean' || isForcedBoolean;
            
            const useNativeCheckbox = isBoolean && !hasCustomRenderer && enableBooleanCheckboxes;

            const { component, params } = this.getColumnRendererConfig(k, isBoolean, hasCustomRenderer, enableBooleanCheckboxes);

            const colDef: ColDef = {
                field: k,
                headerClass: this.formatterHeaderClass.bind(this),
                sortable: (!!v && typeof v === 'object') ? v?.sortable : this.sortingsMenuDisabled(k),
                valueGetter: this.getterValue.bind(this),
                headerValueGetter: this.localizeHeader.bind(this),
                valueFormatter: useNativeCheckbox ? undefined : this.formatterValue.bind(this),
                cellClass: this.formatterExcel.bind(this),
                suppressHeaderMenuButton: isInternalFilterDisabled || this.filtersMenuDisabled(k),
                cellStyle: (params) => this.formatterCellStyle(params),
                editable: useNativeCheckbox && !booleanCheckboxesReadOnly,
                ...(isInternalFilterDisabled 
                    ? { filter: false } 
                    : filterType(k, v, this.formatterValue.bind(this)))
            }

            if (useNativeCheckbox) {
                Object.assign(colDef, this.getForcedBooleanHandlers(k, isForcedBoolean));
            }

            if (isClientSide) {
                colDef.cellRendererSelector = (p) => (p.data && Object.keys(p.data).length > 0)
                    ? { component, params }
                    : { component: SkeletonGridRowComponent };
            } else {
                colDef.cellRenderer = component;
                colDef.cellRendererParams = params;
            }


            return {...this.defaultColDef, ...colDef};
        });

        if (checkbox.visible) {
            const checkboxCol: ColDef = {
                colId: 'checkbox',
                headerCheckboxSelection: (params: any) => {
                    const readOnly = params.context?.readOnlyTable;
                    return !readOnly && this.headerCheckboxSelection;
                },
                headerCheckboxSelectionFilteredOnly: true,
                checkboxSelection: (params: any) => {
                    const readOnly = params.context?.readOnlyTable;
                    return !readOnly && !!params.data;
                },
                cellClass: (params: any) => {
                    return params.context?.readOnlyTable ? 'tw-opacity-30 tw-pointer-events-none' : '';
                },
                showDisabledCheckboxes: true,
                // headerComponent: CustomGridCheckboxAllComponent,
                lockVisible: true,
                pinned: checkbox.lockPosition,
                minWidth: 50,
                maxWidth: 55,
                lockPosition: checkbox.lockPosition,
                filter: false,
                sortable: false,
                suppressHeaderMenuButton: true,
                suppressMovable: true,
                enableRowGroup: false,
                enablePivot: false,
                resizable: false
            }

            if (isClientSide) {
                checkboxCol.cellRendererSelector = (params) => {
                    if (!params.data || Object.keys(params.data).length === 0) {
                        return { component: SkeletonGridRowComponent };
                    }
                    return undefined;
                };
            }
            this.columnDefs.unshift(checkboxCol);
        }
        if (customButton?.visible) {
            const actionCol: ColDef = {
                headerName: this.headerAction,
                field: this.headerAction,
                lockVisible: customButton.visible,
                colId: 'params',
                editable: false,
                minWidth: 62,
                maxWidth: 70,
                resizable: false,
                // headerClass: "actionsM",
                // cellClass: "actionsC",
                lockPosition: customButton.lockPosition,
                pinned: customButton.lockPosition,
                filter: false,
                sortable: false,
                suppressHeaderMenuButton: true,
                suppressMovable: true,
                enableRowGroup: false,
                enablePivot: false,
                headerValueGetter: this.localizeHeader.bind(this)
            }

            if (isClientSide) {
                actionCol.cellRendererSelector = (params) => {
                    if (params.data && Object.keys(params.data).length > 0) {
                        return { component: customButton.cellRenderer, params: customButton.cellRendererParams };
                    }
                    return { component: SkeletonGridRowComponent };
                };
            } else {
                actionCol.cellRenderer = customButton.cellRenderer;
                actionCol.cellRendererParams = customButton.cellRendererParams;
            }

            this.columnDefs.push(actionCol);
        }
        this.localText = this.getGridLanguage();
        this.debugLog('GRID COLUMN DEFS', this.columnDefs);

        // console.log("COLUMN DEFS ", this.columnDefs)
    }

    /**
     * Opzioni comuni (resizable, sortable, filterParams, menuTabs...) da riusare nelle detail grid.
     * Esclude il cellRenderer di loading, valido solo per il master.
     */
    get detailDefaultColDef(): ColDef {
        return { ...this.defaultColDef, cellRenderer: undefined };
    }

    /**
     * Costruisce i ColDef di una detail grid a partire da un TableData, con la stessa logica
     * del master (header tradotti, valueGetter, valueFormatter, filtro per tipo di dato).
     * Le detail sono sempre client-side, quindi filtri e ordinamenti sono quelli nativi di ag-grid.
     */
    buildDetailColumnDefs(detail: TableData): ColDef[] {
        const dataObj = detail.dataType as any;
        const keys = detail.columnsOrder || Object.keys(dataObj);

        return keys.map((k: string) => {
            const v = this.getDeepValue(dataObj, k);
            const valueGetterFn = detail.columnsValueGetter?.get(k);
            const valueFormatterFn = detail.columnsFormatter?.get(k);
            const formatter = (p: any) => valueFormatterFn ? valueFormatterFn(p) : p.value;

            const colDef: ColDef = {
                field: k,
                headerValueGetter: () => {
                    const headerIdentifier = detail.headersTranslate.get(k);
                    return headerIdentifier ? this.localizationService.translate(headerIdentifier) : k;
                },
                valueGetter: (p: any) => valueGetterFn ? valueGetterFn(p) : this.getDeepValue(p.data, k),
                valueFormatter: formatter,
                ...filterType(k, v, formatter)
            };

            return { ...this.detailDefaultColDef, ...colDef };
        });
    }

    private getForcedBooleanHandlers(key: string, isForced: boolean) {
        return {
            valueGetter: (params: any) => {
                const val = this.getterValue(params);
                if (isForced) {
                    return val === 'simple';
                }
                return !!val; 
            },
            valueSetter: (params: any) => {
                if (isForced) {
                    if(key === 'stock_type') params.data[key] = params.newValue ? 'simple' : null;
                } else {
                    params.data[key] = params.newValue;
                }
                return true;
            }
        };
    }

    private getColumnRendererConfig(key: string, isBoolean: boolean, hasCustom: boolean, useCheckboxes: boolean) {
        const rendererEntry = this._tableData.cellComponentRenderer?.get(key);
        
        if (hasCustom) {
            return {
                component: (rendererEntry as any)?.component || rendererEntry,
                params: (rendererEntry as any)?.params || {}
            };
        }
        
        if (isBoolean && useCheckboxes) {
            const isReadOnly = this._tableData.booleanCheckboxesReadOnly || false;
            return {
                component: 'agCheckboxCellRenderer',
                params: { readOnly: isReadOnly }
            };
        }
        
        return { component: undefined, params: {} };
    }

    private getDeepValue(obj: any, path: string): any {
        if (!obj || !path) return undefined;
        return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj);
    }

    private getDeepValueWithArray(obj: any, path: string): any {
        if (!obj || !path) return undefined;
        const keys = path.split('.');

        const getValue = (current: any, remainingKeys: string[]): any => {
            if (current == null) return undefined;
            if (remainingKeys.length === 0) return current;

            const [key, ...rest] = remainingKeys;

            if (Array.isArray(current)) {
                return current
                    .map(item => getValue(item[key], rest))
                    .filter(v => v !== undefined);
            }

            const nextValue = current[key];
            
            if (Array.isArray(nextValue) && rest.length > 0) {
                return nextValue
                    .map(item => getValue(item, rest))
                    .flat();
            }

            return getValue(nextValue, rest);
        };

        return getValue(obj, keys);
    }

    private setDeepValue(obj: any, path: string, value: any) {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const lastObj = keys.reduce((acc, key) => (acc ? acc[key] : undefined), obj);
        if (lastObj && lastKey) {
            lastObj[lastKey] = value;
        }
    }

    // Each Column Definition results in one Column.
    columnDefs: ColDef[] = [];

    // DefaultColDef sets props common to all Columns
    defaultColDef: ColDef = {
        flex: 1,
        resizable: true,
        minWidth: 100,
        sortable: true,
        filter: true,
        filterParams: {
            buttons: ['reset', 'apply'],
            closeOnApply: true,
            maxNumConditions: 1,
            excelMode: 'windows'
        },
        suppressMovable: false,
        menuTabs: ['filterMenuTab'],
        // allow every column to be aggregated
        enableValue: false,
        // allow every column to be grouped
        enableRowGroup: false,
        // allow every column to be pivoted
        enablePivot: false,
        cellRenderer: this.loadingCellRenderer,
        // loadingCellRenderer: this.loadingCellRenderer
    };

    rowClassRules: any;
    private previousNodeSelected?: IRowNode;
    private nodeSelected?: IRowNode;
    private gridRowsRendered = false;
    private resetSelection = false;
    idsToRestore: string[] = [];
    overlayNoRowsTemplate = '<div class="no-rows-to-show" translate>No Results To Show</div>';

    onModelUpdated(event: ModelUpdatedEvent) {
        this.agGrid.firstDataRendered.pipe(first()).subscribe(_ => this.gridRowsRendered = true);
        if (this.gridRowsRendered) {
            event.api.getDisplayedRowCount() === 0
                ? event.api.showNoRowsOverlay()
                : event.api.hideOverlay();
        }

        if (!this.idsToRestore || this.idsToRestore.length === 0) return;

        const api = event.api;
        let restoredCount = 0;

        api.forEachNode((node: any) => {
            if (node.data && this.idsToRestore.includes(node.data.uuid)) {
                node.setSelected(true);
                restoredCount++;
            }
        });

        const rowCount = api.getDisplayedRowCount();
        const firstRow = api.getDisplayedRowAtIndex(0);
        
        if (rowCount > 0 && firstRow && firstRow.data !== null) {
            // console.log(`Ripristinate ${restoredCount} selezioni`);
            this.idsToRestore = []; 
        }

    };

    sideBar: SideBarDef | string | string[] | boolean | null = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                minWidth: 340,
                width: 340,
                maxWidth: 400,
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivots: true,
                    suppressPivotMode: true,
                },
            },
            {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
                minWidth: 340,
                maxWidth: 400,
                width: 340,
            },
        ],
        position: 'left',
        hiddenByDefault: true,
        defaultToolPanel: 'Filters',
    };

    statusBar: {
            statusPanels: StatusPanelDef[];
        } = {
        statusPanels: [
            {
                statusPanel: GridStatusBarComponent,
            }
        ]
    }

    public excelStyles: ExcelStyle[] = [
        {
            id: 'dateFormat',
            dataType: 'DateTime',
            numberFormat: {
                format: 'DD/MM/YY HH:mm',
            },
        },
        {
            id: 'currencyFormat',
            numberFormat: {
                format: '#,##0.00 ' + TilbyCurrencyPipe.currency.symbol_native,
            },
        },
        {
            id: 'percentuageFormat',
            numberFormat: {
                format: '0%',
            },
        },
        {
            id: 'forceText',
            dataType: 'String'
        }
    ]

    private debugLog(...args: any[]) {
        DevLogger.debug('[ GridComponent ] ',...args);
    }

    getRowId: GetRowIdFunc = (params: GetRowIdParams) =>  String(params.data?.uuid || params.data?.[this.idField || 'id'])

    ngOnInit() {
        this.columnDefsInitial = this.columnDefs.map(c => ({ ...c }));
        this.columnsControl.setValue([...this.columnDefsInitial.map(c => c.field)]);
        this.columnsControl.valueChanges.pipe(this.onDestroyService.takeUntilDestroy, tap(this.debugLog)).subscribe(columnsToShow => this.columnDefs = this.columnDefsInitial.filter(c => columnsToShow.indexOf(c.field) > -1));
    }

    // Il metodo  gestisce la configurazione delle colonne della griglia in base alle preferenze dell'utente e/o alla configurazione predefinita
    setColumnsPreferences() {
        if (!this.gridApi) return;

        const columnsPreferenceDefault = this.feature && this.gridUtils.getConfiguration(
            `grid.${this.feature}.${this.getMobile()}.columns`, 
            this.feature, 
            0
        );

        let columnStatePreference = this.configurationManager.getPreference(
            `grid.${this.feature}.${this.getMobile()}.columns`
        ) || undefined;

        if (!columnStatePreference) {
            columnStatePreference = columnsPreferenceDefault;
        }

        if (columnStatePreference) {
            try {
                const columnStatePreferenceObj = JSON.parse(columnStatePreference);
                const colDefsMap = keyBy(this.columnDefs, d => d.colId || d.field);

                columnStatePreferenceObj.forEach((savedCol: any) => {
                    const colDef = colDefsMap[savedCol.colId];

                    if (colDef) {
                        colDef.width = savedCol.width;
                        colDef.minWidth = savedCol.minWidth;
                        colDef.flex = savedCol.flex;
                    }
                });

                const processedState = this.getDynamicColumnState(columnStatePreferenceObj, colDefsMap);

                if (processedState && processedState.length > 0) {
                    this.gridApi?.applyColumnState({
                        state: processedState,
                        applyOrder: true,
                    });
                }
            } catch (e) {
                // console.error("Errore nel parsing delle preferenze colonne", e);
                this.gridApi.resetColumnState();
            }
        } else {
            this.gridApi.resetColumnState();
        }
    }

    onToolPanelVisible(event: ToolPanelVisibleChangedEvent) {
        if (!event.source)
            this.gridApi?.setSideBarVisible(false);
    }

    // Example load data from sever
    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.rowClassRules = {
            'ag_grid-row-is-deleting tw-pointer-events-none': (params: any) => this.gridService.tableProperties.idsDeleting.indexOf(params.data?.id) > -1,
            'ag-row-is-clickable': (params: any) => {
                const isClickableInConfig = this._tableData.isRowClickable !== false;
                return isClickableInConfig;
            },
            'is-loading-row': (params: any) => !params.data,
        };
        this.translateService.onLangChange.pipe(this.onDestroyService.takeUntilDestroy).subscribe(() => {
            this.gridApi?.refreshHeader();
            this.gridApi?.refreshToolPanel();
        });
        
        if(this.gridApi) {
            // const hasPrefs = !!this.configurationManager.getPreference(this.getConfigurationColumnsPreferences());
            this.gridApi.sizeColumnsToFit();
            this.gridApi.refreshHeader();
            this.gridApi.refreshToolPanel();
            this.gridApi.setGridOption('suppressCellFocus', true);
        }
        this.gridReadyEmitter.emit();
    }

    onFirstDataRendered(params: FirstDataRenderedEvent) {
        this.firstDataRenderedEmitter.emit();
    }

    onFilterChanged($event: FilterChangedEvent) {
        // console.log("onFilterChanged ", $event);
        // console.log("FILTER MODEL ", this.gridApi?.getFilterModel());

        if (!this.gridApi || this.gridApi.isDestroyed()) {
            return;
        }

        const selectedNodes = this.gridApi.getSelectedNodes();
        if (selectedNodes && selectedNodes.length > 0) {
            this.gridApi.deselectAll();
        }

        this.filterChangedEmitter.emit($event);
    }

    getRowClass = (params: any) => {
        if (this.selectRowUIEnabled) {
            const rowNode = params.node;
            if (!this.resetSelection && rowNode === this.nodeSelected) {
              return 'ag-row-background__selected';
            }
            return 'ag-row-background__unselected';
        }
    }

    gridReady(nodeSelected: ((itemsRowAdded: any) => IRowNode | undefined)) {
        // @ts-ignore
        this.gridService.tableProperties.gridIstances[this.agGrid?._nativeElement.id] = this.agGrid;
// eg. this.gridService.customers$
        (this.gridService[`${this.feature!}$`].flash as Observable<any>)
            .pipe(this.onDestroyService.takeUntilDestroy)
            .subscribe((itemsRow) => {
                this.debugLog('COMBINE READY');
                const {api: gridApi} = this.agGrid;
                const {isNew = false, ...itemsRowAdded} = itemsRow || {};
                setTimeout(() => {
                    this.debugLog('GRID_OPTIONS', this.agGrid.gridOptions);
                    let counter = 0;
                    while (((!this.nodeSelected || this.nodeSelected.id != itemsRowAdded.id) && counter <= 200)) {
                        this.nodeSelected = nodeSelected(itemsRowAdded);
                        this.debugLog(counter, 'WHILE', this.nodeSelected);
                        counter++;
                    }
                    if (isNew) {
                        this.nodeSelected = this.nodeSelected || nodeSelected(itemsRowAdded);
                        setTimeout(() => gridApi.ensureIndexVisible(this.nodeSelected?.rowIndex || 0, 'bottom'))
                    }
                    if (itemsRow) this.nodeSelected?.setData(itemsRowAdded); //update grid rendering
                    // 2023-06-19 - Remove flash in grid
                    // setTimeout(_ => (!!this.nodeSelected) && gridApi.flashCells({
                    //     fadeDelay: 1000,
                    //     rowNodes: [this.nodeSelected],
                    // }));
                });
            })
    }


    //--------DEVICE AND ORIENTATION-------------

    // getOrientation(): string {
    //     return this.orientation.includes('landscape') ? 'landscape' : 'portrait';
    // }

    getMobile(): string {
        return mobileCheck() ? 'mobile' : 'tablet';
    }

    //---------------COLUMNS---------------------

    onColumnMoved(params: any) {
        if (params.source !== 'uiColumnMoved' && params.source !== 'toolPanelUi') {
            this.setColumnsPreferences();
        } else {
            if (!this.isToastShow) {
                this.showUserPreferenceToast();
            }
        }

    }

    onColumnVisible(params: any) {
        if (!this.isToastShow && params.source === "toolPanelUi") {
            this.showUserPreferenceToast();
        }
        this.handleFlexVsScroll();
    }

    onColumnSortChanged(params: any) {
        this.sortChangedEmitter.emit(params);
        if (!this.isToastShow && params.source === "uiColumnSorted") {
            this.showUserPreferenceToast();
        }
    }

    onColumnResized(params: any) {
        if ((params.source === "uiColumnDragged" || params.source === "uiColumnResized") && params.finished) {
            const currentState = this.gridApi?.getColumnState();
            currentState?.forEach((s :any) => {
                const colDef = this.columnDefs.find(d => (d.colId || d.field) === s.colId);
                if (colDef) {
                    colDef.width = s.width;
                    delete colDef.flex; 
                }
            });
            this.handleFlexVsScroll();
            if(!this.isToastShow) this.showUserPreferenceToast();
        }
    }

    private handleFlexVsScroll() {
        if (!this.gridApi) return;

        const currentState = this.gridApi?.getColumnState();
        if(!currentState) return;

        const processedState = this.getDynamicColumnState(currentState);
        this.gridApi?.applyColumnState({
            state: processedState,
            applyOrder: true
        });
    }

    private getDynamicColumnState(currentState: ColumnState[], colDefsDict?: Record<string, ColDef> ): ColumnState[] {
        if (!this.agGridElement || !this.gridApi) return currentState;

        const gridWidth = this.agGridElement.nativeElement.offsetWidth;
        if (gridWidth === 0) return currentState;

        const pinnedWidth = currentState
            .filter(c => c.pinned && !c.hide)
            .reduce((sum, c) => sum + (c.width || 0), 0);

        const availableCenterWidth = gridWidth - pinnedWidth;
        const centerCols = currentState.filter(c => !c.pinned && !c.hide);

        const getColInfo = (c: any) => {
            const colId = c.colId || c.field;

            const def = colDefsDict
                ? colDefsDict[c.colId]
                : this.columnDefs.find(d => (d.colId || d.field) === colId);
            
            return {
                minWidth: (def?.minWidth as number) || 100,
                nominalWidth: (def?.width as number) || (def?.minWidth as number) || 100,
                flexWeight: def?.flex || null
            };
        };

        const totalNominalWidth = centerCols.reduce((sum, c) => sum + getColInfo(c).nominalWidth, 0);
        const isOverflowing = totalNominalWidth > (availableCenterWidth + 5);

        const totalWeight = centerCols.reduce((sum, c) => {
            const info = getColInfo(c);
            return sum + (info.flexWeight || (info.nominalWidth / 100));
        }, 0);

        return currentState.map((col: any) => {
            const colId = col.colId || col.field;

            const originalDef = colDefsDict
                ? colDefsDict[col.colId]
                : this.columnDefs.find(d => (d.colId || d.field) === colId);

            if (col.colId === 'checkbox' || col.colId === 'params' || col.pinned) {
                return { 
                    ...col, 
                    flex: null,
                    ...(originalDef && {
                        pinned: originalDef.lockPosition || col.pinned,
                        width: (col.width || originalDef.width || originalDef.minWidth) as number
                    })
                };
            }

            const targetPinned = originalDef?.lockPosition || null;
            const info = getColInfo(col);

            if (isOverflowing) {
                return { ...col, flex: null, width: info.nominalWidth, pinned: targetPinned };
            } else {
                const weight = info.flexWeight || (info.nominalWidth / 100);
                const rawFlex = totalWeight > 0 ? (weight / totalWeight) * 100 : 1;
                const proportionalFlex = Math.round(rawFlex * 10) / 10;
                
                return { ...col, flex: proportionalFlex, pinned: targetPinned };
            }
        });
    }

    refreshHeader() {
        if (this.gridApi && !this.gridApi.isDestroyed()) {
            this.gridApi.refreshHeader();
        }
    }

    resetLayout() {
        // this.gridApi?.resetColumnState();
        this.configurationManager.deleteUserPreference(this.getConfigurationColumnsPreferences());
        this.setColumnsPreferences();
    }

    showUserPreferenceToast() {
        this.isToastShow = !this.isToastShow;
        this.toastSaveUserPreference = this._snackBar.openFromComponent(GridSaveUserPreferenceComponent, {
            verticalPosition: "bottom",
            horizontalPosition: "left",
            panelClass:['mat-snack-bar-tilby']
        });

        this.toastSaveUserPreference.afterDismissed().subscribe((event) => {
            if (!event.dismissedByAction) {
                this.setColumnsPreferences();
            }
            this.isToastShow = !this.isToastShow;
        });
        
        this.toastSaveUserPreference.onAction().subscribe(() => {
            if(!this.gridApi) {
                return;
            }

            this.saveColumnState();
        });
    }

    saveColumnState() {
        if (!this.gridApi) {
            return;
        }

        const currentState = this.gridApi.getColumnState() ?? [];
        const colDefsDict = keyBy(this.columnDefs, c => c.colId || c.field);

        const stateToSave = this.getDynamicColumnState(currentState, colDefsDict);

        this.configurationManager.setUserPreference(
            this.getConfigurationColumnsPreferences(), 
            JSON.stringify(stateToSave)
        );
    }

    getConfigurationColumnsPreferences(): `grid.${string}.${string}.columns` {
        // return `grid.${this.feature}.${this.getMobile()}.${this.getOrientation()}.columns`;
        return `grid.${this.feature}.${this.getMobile()}.columns`;
    }

    async openDialogSaveColumnsPreference() {
        const res = await this.confirmDialogService.openDialog({
            data: {
                messageLabel: 'DIALOG.SAVE_SHOP_PREFERENCE.TITLE',
                confirmLabel: 'APPLICATION.YES',
                cancelLabel: 'APPLICATION.NO'
            }
        });

        if(res) {
            this.saveShopColumnsPreference();
        }
    }

    @autobind
    saveShopColumnsPreference(): void {
        const columnState = this.gridApi?.getColumnState();

        // console.log("saveShopColumnsPreference ", columnState)

        if (!columnState) {
            return;
        }

        this.configurationManager.setShopPreference(this.getConfigurationColumnsPreferences(), JSON.stringify(columnState));
    }

    onFilterTextBoxChanged<T extends TableRow = any>(
        filterText?: string, 
        filterableColumns?: (keyof T)[] 
    ) {
        const filterValue = (filterText || '').toLowerCase().trim();

        if (!filterValue) {
            this.gridApi?.setGridOption('isExternalFilterPresent', () => false);
            this.gridApi?.onFilterChanged();
            return;
        }

        if (this.agGrid.rowModelType === 'clientSide') {
            const columnsToFilter = filterableColumns || (Object.keys(this.tableData.dataType) as (keyof any)[]);
            
            this.gridApi?.setGridOption('isExternalFilterPresent', () => true);

            this.gridApi?.setGridOption('doesExternalFilterPass', (node) => {
                const data = node.data;
                if (!data) return false;
                
                return columnsToFilter.some(col => {
                    const value = this.getDeepValueWithArray(data, col as string);
                    if (value == null) return false;

                    if (Array.isArray(value)) {
                        return value.some(v => 
                            v != null && String(v).toLowerCase() == filterValue
                        );
                    }

                    return String(value).toLowerCase().includes(filterValue);
                });
            });
        }
        else {
            this.gridApi?.setFilterModel(null);
        }
        this.gridApi?.onFilterChanged();
    }

    onRowOrExternalClicked(event: RowClickedEvent | RowNode) {
        if (!this.gridApi || this.gridApi.isDestroyed()) return;

        const rowData = 'data' in event ? event.data : (event as any).data;
        if (!rowData) {
            return;
        }

        const isClickableInConfig = this._tableData.isRowClickable !== false;
        if (!isClickableInConfig) {
            return;
        }

        this.resetSelection = false;
        this.previousNodeSelected = this.nodeSelected;
        this.nodeSelected = 'node' in event ? event.node : event;

        if (this.previousNodeSelected) {
            this.gridApi?.redrawRows({ rowNodes: [this.previousNodeSelected] });
        }

        this.gridApi?.redrawRows({ rowNodes: [this.nodeSelected] });

        if ('node' in event) {
            const rowEvent = event as RowClickedEvent;
            const target = rowEvent.event?.target as HTMLElement;
            const colId = target?.closest('.ag-cell')?.getAttribute('col-id');
            if (colId === 'checkbox') {
                return;
            }

            this.rowClicked.emit(event as RowClickedEvent);
        }
    }

    resetUIRows() {
        this.resetSelection = true;
        if (this.nodeSelected) {
            this.gridApi?.redrawRows({ rowNodes: [this.nodeSelected] });
        }

        this.previousNodeSelected = undefined;
        this.nodeSelected = undefined;
    }

    //---------------EXPORT---------------------

    onBtnExport($event?: DataExport,tableToCall?:string,extraQueryParams?:Record<string,any>) {
        if ($event) {
            // let rowsSelected = !!this.gridApi?.getSelectedRows().length;
            let rowsSelected = !!this.getSelectedCount();
            
            return rowsSelected ? this.clientSideExport($event, rowsSelected) : this.serverSideExport($event,tableToCall,extraQueryParams);
        }
    }

    public getSortFilterQuery():Record<string, string> {
        const filters = this.agGrid.api.getFilterModel();
        const sortBy = this.agGrid.api.getColumnState().find(col=>!!col.sort);
        const filterBy = Object.keys(filters).reduce((acc, colName) => ({
                ...acc,
                ...queryFilteredOrdered_FEtoBEConverter(acc, colName as KeysOfUnion<TableRow>, filters[colName], this.tableData.dataType)
            }),
            {} as any);
        this.log('GET_SORT_FILTER_QUERY', {filters,sortBy});
        return {
            // ...((sortBy?.colId) && {[`orderby_${sortBy?.sort}`]: sortBy?.colId}), SORTBY_DISATTIVATO (Non implementato a BE)
            ...filterBy
        };
    }

    async serverSideExport($event?: DataExport,tableToCall?:string,extraQueryParams?:Record<string,any>) {
        if(!$event) {
            return this.error('Something went wrong!');
        }
        this.setGridExportUserPreferenceByFeature($event);
        const {data=[],type} = $event ||{};
        const format = type==='excel'?'xlsx':'csv';
        const columnsName = data.reduce((s,a) => [ ...s, ...(a.isChecked?[a.colField]:[])] as string[],[] as string[]).join(',');  //filter(col => col.isChecked).map(col => col.colField)

        const query= this.getSortFilterQuery();

        this.log('SERVER_SIDE_EXPORT', {columnsName,format,query});
        let messageToBePrinted='';
        try{
            await this.restMangerService.post(`massive_export/file`,undefined, {...query, table:tableToCall||this.feature, columns:columnsName, format, ...extraQueryParams});
            messageToBePrinted='DONE';
        }
        catch(error){
            messageToBePrinted='ERROR';
        }
        finally {
            this.alertDialogService.openDialog({data:{messageLabel:`DIALOG.SERVER_SIDE_EXPORT.${messageToBePrinted}`}});
        }
    }

    async clientSideExport($event: DataExport, rowsSelected: boolean) {
        this.setGridExportUserPreferenceByFeature($event);

        const exportPromise = new Promise((resolve, reject) => {
            try {
                let exportData: any = {
                    columnKeys: $event.data.map(h => h.colField),
                    onlySelected: rowsSelected,
                    processCellCallback: (params: any) => this.defaultExportFormatter(params)
                };

                if ($event.type === 'csv') {
                    this.gridApi?.exportDataAsCsv(exportData);
                } else {
                    this.gridApi?.exportDataAsExcel(exportData);
                }

                resolve(true);
            } catch (error) {
                console.error("Errore durante l'esportazione client-side:", error);
                reject(error);
            }
        });

        await this.waitDialogService.openDialog({
            data: {
                message: 'APPLICATION.EXPORTING_DATA',
                promise: exportPromise,
                showProgress: true
            }
        });
    }

    async massiveExportFromLocalStorage(dataExport: DataExport,
        preSelectedRows?: any[]
    ) {
        const exportPromise = new Promise(async (resolve, reject) => {
            try {
                const dataToLoad = preSelectedRows ? preSelectedRows : (this.itemDataCatalogService.getDataByKey(this.feature as any) || []);
                const currentSort = this.gridApi?.getColumnState();

                // Pulizia delle colonne: rimozione dei renderer
                const sanitizedColumnDefs = this.columnDefs.map(col => {
                    const { cellRenderer, cellRendererParams, ...rest } = col;
                    return rest; 
                });

                // Creazione di un div invisibile
                const tempDiv = document.createElement('div');
                tempDiv.style.display = 'none';
                document.body.appendChild(tempDiv);

                // Configurazione della griglia temporanea in modalità CLIENT-SIDE
                const tempGridOptions: GridOptions = {
                    columnDefs: sanitizedColumnDefs,
                    rowData: dataToLoad,
                    rowModelType: 'clientSide',
                    excelStyles: [ { id: 'forceText', dataType: 'String' } ],
                    context:  {
                        ...this.gridApi?.getGridOption('context'),
                        allData: true
                    },
                    onGridReady: (params) => {
                        try {
                            // Applicazione dell'ordinamento se presente
                            if (currentSort) {
                                params.api.applyColumnState({ state: currentSort, applyOrder: true });
                            }

                            setTimeout(() => {
                                // ESPORTAZIONE solo delle righe che passano i filtri applicati
                                const exportParams = {
                                    columnKeys: dataExport.data.map(h => h.colField),
                                    onlySelected: false, 
                                    processCellCallback: (params: any) => this.defaultExportFormatter(params)
                                };
    
                                params.api.exportDataAsExcel(exportParams);
    
                                // PULIZIA
                                params.api.destroy();
                                document.body.removeChild(tempDiv);
    
                                resolve(true);
                            })
                        } catch (err) {
                            reject(err);
                        }
                    }
                };

                // Inizializza la griglia
                createGrid(tempDiv, tempGridOptions);

            } catch (error) {
                console.error("Errore durante l'esportazione massiva:", error);
                reject(error);
            }
        });

        await this.waitDialogService.openDialog({
            data: {
                message: 'APPLICATION.EXPORTING_DATA',
                promise: exportPromise,
                showProgress: true
            }
        });
    }

    private defaultExportFormatter(params: any): any {
        const colId = params.column.getColId();
        const value = params.value;
        
        // Se l'utente ha passato un formatter custom via Input, usiamo quello
        if (this.exportFormatterOptions) {
            return this.exportFormatterOptions(params);
        }

        // Altrimenti, cerchiamo automaticamente la Map nel servizio usando la feature
        if (this.feature) {
            const formattersMap = (this.gridCellFormatterService as any)[this.feature];

            if (formattersMap instanceof Map) {
                const formatterFn = formattersMap.get(colId);
                if (formatterFn) {
                    return formatterFn({
                        value: value,
                        data: params.node?.data,
                        column: params.column,
                        api: params.api,
                        context: params.context
                    });
                }
            }
        }

        // Fallback: restituisce il valore originale
        return value;
    }

    public setExportPreferenceFromVisibleColumns() {
        const columns = (this.gridApi?.getColumnState() || [])
            .filter(col => !col.hide && col.colId !== 'checkbox')
            .map(col => col.colId);
        this.configurationManager.setUserPreference(`grid.${this.feature}.export`, JSON.stringify({columns}));
    }

    protected setGridExportUserPreferenceByFeature($event: DataExport){
        let json = {columns: $event.data.map(col => col.colField)}
        this.configurationManager.setUserPreference(`grid.${this.feature}.export`, JSON.stringify(json));
    }

    //------------TRANSACTION-------------------

    @autobind
    updateItems<T extends SuppliersFields|CustomersFields|PromotionsFe>(cellsToUpdated: T) {
        const updatedRows: TableRowUpdatable[] = [];
        this.gridApi?.forEachNode(rowNode => {
            if (rowNode.isSelected() === false) {
                return;
            }
            const updatedRow = {...rowNode.data, ...cellsToUpdated}
            rowNode.setData(updatedRow);
            updatedRows.push(updatedRow);
        });
        this.updateChange.emit(updatedRows);
    }

    printResult(res: RowNodeTransaction | ServerSideTransactionResult) {
        this.debugLog('---------------------------------------', res);
        if (res.add) {
            res.add.forEach((rowNode) => {
                this.debugLog('Added Row Node', rowNode);
            });
        }
        if (res.remove) {
            res.remove.forEach((rowNode) => {
                this.debugLog('Removed Row Node', rowNode);
            });
        }
        if (res.update) {
            res.update.forEach((rowNode) => {
                this.debugLog('Updated Row Node', rowNode);
            });
        }
    }

    //-----------------SIDEBAR------------------

    openToolPanel(panel: GridPanel) {
        const otherPanelOpened = this.gridApi?.getOpenedToolPanel() !== panel;
        if (!this.gridApi?.isSideBarVisible() || otherPanelOpened) {
            this.gridApi?.setSideBarVisible(true);
            this.gridApi?.openToolPanel(panel)
        } else this.gridApi?.setSideBarVisible(false);

    }

    advancedFilterSwitch(advFilter: boolean) {
        this.defaultColDef = {
            ...this.defaultColDef,
            menuTabs: advFilter
                ? ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab']
                : ['filterMenuTab']
        };
    }

    columnMovableSwitch(movColumns: boolean) {
        if (!this.gridApi || this.gridApi.isDestroyed()) return;

        this.gridApi.setGridOption('suppressMovableColumns', !movColumns);
        this.gridApi.refreshHeader();

        // this.defaultColDef = {
        //     ...this.defaultColDef,
        //     suppressMovable: !movColumns,
        // };

        // TODO: Temp solution - wait ticket ag-grid
        // for (let i = 0; i < this.columnDefs.length; i++) {
        //     this.columnDefs[i]["suppressMovable"] = !movColumns;
        // }
        // this.gridApi?.setGridOption('columnDefs', this.columnDefs);
        // this.setColumnsPreferences();

        // setTimeout(() => {
        //     document.getElementsByTagName('body')[0].classList?.[movColumns ? 'add' : 'remove']('grid-columns-movable');
        //   }, 200);
    }

    //-----------------TRANSLATE------------------

    localizeHeader(parameters: HeaderValueGetterParams): string {
        const field = (<ColDef>parameters.colDef)?.field || '';
        const headerIdentifier = this._tableData.headersTranslate.get((<ColDef>parameters.colDef)?.field || '');
        if (headerIdentifier) {
            return this.localizationService.translate(headerIdentifier);
        }
        return field;
    }

    getterValue(parameters: any): any {
        const field = parameters.colDef.field;

        let valueGetterFn = this._tableData.columnsValueGetter?.get(field || '');
        if (valueGetterFn) {
            return valueGetterFn(parameters);
        }

        return this.getDeepValue(parameters.data, field);
        // return parameters.data ? parameters.data[parameters.colDef.field] : null;
    }

    formatterValue(parameters: any): any {
        let valueFormatter = this._tableData.columnsFormatter?.get(parameters.colDef?.field || '');
        return valueFormatter ? valueFormatter(parameters) : parameters.value;
    }

    formatterHeaderClass(parameters: any): string {
        const isMovableEnabled = this.gridApi ? (this.gridApi.getGridOption("suppressMovableColumns") === false) : false;
        const isMovable = parameters.colDef.suppressMovable === false && 
            !this.gridApi?.getGridOption("suppressMovableColumns") &&
            isMovableEnabled ? 'header-movable' : '';
        const compactClass = this._tableData.isCompact ? 'compact-header' : '';
        return `${isMovable} ${compactClass}`.trim();
    }

    formatterExcel(parameters: CellClassParams): string[] {
        const classes: string[] = [];
        
        const serviceClass = this._tableData.columnsExcelFormatter?.get(parameters.colDef?.field || '');
        if (serviceClass) classes.push(serviceClass);

        if (this._tableData.isCompact) {
            classes.push('compact-cell');
        }

        const value = parameters.value;
        if (typeof value === 'string' && value.startsWith('0')) {
            classes.push('forceText'); 
        }

        return classes;
    }
    
    formatterCellStyle(parameters: CellClassParams): any {
        const styleProvider = this._tableData.columnsStyle?.get(parameters.colDef?.field || '');
        return styleProvider ? styleProvider(parameters) : null;
    }

    filtersMenuDisabled(key: string): boolean | undefined {
        return typeof this._tableData.filtersMenuDisabled === 'boolean' ? this._tableData.filtersMenuDisabled : this._tableData.filtersMenuDisabled?.get(key) || undefined;
    }

    sortingsMenuDisabled(key: string): boolean | undefined {
        return typeof this._tableData.sortingsMenuDisabled === 'boolean' ? !this._tableData.sortingsMenuDisabled : !(this._tableData.sortingsMenuDisabled?.get(key) || undefined);
    }

    getGridLanguage(): GridTraductions {
        return this._tableData.language ? this._tableData.language : this.gridTraductionsService.getTraduction();
    }

    /**
     * Watch changes in grid selection
     * @returns All the selected rows
     */
    selectionChanged(event: SelectionChangedEvent) {
        // const selectedNodes = this.gridApi?.getSelectedNodes().length;
        // const displayedCount = this.gridApi?.getDisplayedRowCount();
        // const selectedRows = this.gridApi?.getSelectedRows().length;
        // const selectedCount = this.getSelectedCount();

        const selectedCount = this.getSelectedCount();
        if (this.idsToRestore.length > 0 && selectedCount === 0) {
            return; 
        }

        if(this.gridApi?.getGridOption('rowModelType') === 'serverSide') {
            const lastEndRow = (Object.values(this.gridApi?.getCacheBlockState() || {}) as {startRow: number, endRow: number}[])
                .sort((a: any, b: any) => a.startRow - b.startRow)
                .pop()?.endRow;

            this.isSelectAll = selectedCount === lastEndRow;
            Object.assign(this.context, {
                ...this.context,
                extras: {
                    ...this.context.extras,
                    allSelected: this.isSelectAll
                }
            })
            
            // console.log("Stato Seleziona Tutto Globale:", this.isSelectAll);
        }
        // this.areRowsSelected.emit(!!this.gridApi?.getSelectedRows().length);
        this.areRowsSelected.emit(!!this.getSelectedCount());
    }

    onCellValueChanged(params: any) {
        const field = params.colDef.field;
        const rowId = params.node.id;
        const cellKey = `${rowId}_${field}`;

        if (this.pendingCells.has(cellKey)) {
            params.node.isReverting = true;
            params.node.setDataValue(field, params.oldValue);
            setTimeout(() => { params.node.isReverting = false; }, 0);
            return;
        }

        if (params.newValue === params.oldValue || params.node.isReverting) return;
        if (!this.feature) return;

        const featureCamel = this.snakeToCamel(this.feature);
        const manager = (this.entityManagerService as any)[this.feature] || 
                        (this.entityManagerService as any)[featureCamel];

        if (!manager) {
            return;
        }

        const isNativeBoolean = typeof params.newValue === 'boolean';
        const isForcedBoolean = this._tableData.forceBooleanColumns?.includes(field);

        if (isNativeBoolean || isForcedBoolean) {
            let valueToSave = params.newValue;
            if(isForcedBoolean) {
                const mapping = this._tableData.booleanMappings?.[field];
                if(mapping) {
                    valueToSave = params.newValue ? mapping.trueValue : mapping.falseValue;
                }
            }
            this.setDeepValue(params.data, field, valueToSave);

            this.pendingCells.add(cellKey);
            
            if (typeof manager.putOneOnline === 'function') {
                this.saveQueue = this.saveQueue.then(async () => {
                    try {
                        await manager.putOneOnline(params.data);
                        this._snackBar.open(this.translateService.instant('APPLICATION.EDIT_SUCCESS'), undefined, { 
                            duration: 3000, 
                            horizontalPosition: 'start', 
                            verticalPosition: "bottom", 
                        });
                    } catch (err) {
                        // this.error("Errore durante il salvataggio boolean", err);
                        params.node.isReverting = true;
                        params.node.setDataValue(field, params.oldValue);
                        setTimeout(() => { params.node.isReverting = false; });

                        if(this.isErrorDialogOpen) {
                            return;
                        }

                        this.isErrorDialogOpen = true;
                        this.alertDialogService.openDialog({
                            data: { messageLabel: 'APPLICATION.EDIT_ERROR' }
                        }).then(() => {
                            console.log("ERROR nascosto nel THEN");
                            this.isErrorDialogOpen = false;
                        }).catch(() => {
                            console.log("ERROR nascosto nel CATCH");
                            this.isErrorDialogOpen = false;
                        });
                    } finally {
                        this.pendingCells.delete(cellKey);
                    }
                });
            }

        }
    }

    private snakeToCamel(str: string): string {
        return str.replace(/(_\w)/g, (m) => m[1].toUpperCase());
    }

    isSelectAll = false;
    
    onColumnHeaderClicked(event: any) {
        if(this.gridApi?.getGridOption('rowModelType') === 'serverSide') {
            const lastEndRow = (Object.values(this.gridApi?.getCacheBlockState() || {}) as {startRow: number, endRow: number}[])
                .sort((a: any, b: any) => a.startRow - b.startRow)
                .pop()?.endRow || 0;
    
            if (event.column.getColId() === 'checkbox') {
                const selectedCount = this.getSelectedCount();

                this.isSelectAll = selectedCount > 0 && selectedCount <= lastEndRow;
                Object.assign(this.context, {
                    ...this.context,
                    extras: {
                        ...this.context.extras,
                        allSelected: this.isSelectAll
                    }
                })
                
                // console.log("Stato Seleziona Tutto Globale:", this.isSelectAll);
            }
        }
    }

    private isCheckable(k: TableRow): boolean {
        if (this.tableData.isCheckable) {
            return this.tableData.isCheckable(k) ?? true;
        }
        return true;
    }

    getSelectedCount(): number {
        if (!this.gridApi) return 0;
        
        const modelType = this.gridApi.getGridOption('rowModelType');

        if (modelType === 'clientSide') {
            return this.gridApi.getSelectedNodes().length;
        } 
        else {
            const state = this.gridApi.getState().rowSelection as ServerSideRowSelectionState;
            const totalRows = this.gridApi.getDisplayedRowCount();

            if (state?.selectAll) {
                const exceptions = state.toggledNodes?.length || 0;
                return totalRows - exceptions;
            } else {
                return state?.toggledNodes?.length || 0;
            }
        }
    }


    ngOnDestroy() {
        // this.debugLog('GRID DESTROY ');
        this.toastSaveUserPreference?.dismiss();
        if (this.gridApi && !this.gridApi.isDestroyed()) {
            this.gridApi.destroy();
        }
    }
}
